import React from 'react';
import { Doughnut } from 'react-chartjs-2';
import '../../../../lib/chartTheme';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import styles from './AppointmentStatistics.module.css';
import Button from '../../../../components/Button/Button';
import { useTokenColors } from '../../../../lib/useTokenColors';
import { useLanguage } from '../../../../contexts/LanguageContext';

ChartJS.register(ArcElement, Tooltip, Legend);

interface Props {
    labels: string[];
    values: number[]; // percentages 0-100
}

// AB#2193 — categorical palette from tokens (Chart.js draws on canvas); the HTML legend
// bullets use the same resolved values.
// O1 (AB#2199) — the backend buckets are [Pending, Confirm, Rejected, Completed]; they
// are shown with the shared lifecycle words and the founder status colours
// (awaiting confirmation = amber, confirmed = info, cancelled / no-show = red,
// completed = green), matching the Calendar + Appointments pills.
const PALETTE = ['warning-fg', 'info-fg', 'danger-fg', 'success-fg'] as const;
const BUCKET_LABEL_KEY: Record<string, string> = {
    Pending: 'booking.status.pending',
    Confirm: 'booking.status.confirmed',
    Rejected: 'dashboard.stat.cancelledOrNoShow',
    Completed: 'booking.status.completed',
};


const buildData = (labels: string[], values: number[], backgroundColors: string[]) => ({
    labels,
    datasets: [
        {
            data: values,
            backgroundColor: backgroundColors,
            borderWidth: 0,
        },
    ],
});

const doughnutOptions = {
    maintainAspectRatio: false,
    cutout: '60%',
    plugins: {
        legend: {
            display: false,
        },
    },
};

const AppointmentStatistics: React.FC<Props> = ({ labels: bucketLabels, values }) => {
    const { t } = useLanguage();
    const tc = useTokenColors(PALETTE);
    const backgroundColors = PALETTE.map((n) => tc[n]);
    const labels = bucketLabels.map(l => (BUCKET_LABEL_KEY[l] ? t(BUCKET_LABEL_KEY[l]) : l));
    return (
        <div className={styles.appointmentStatistics}>
            <div className={styles.header}>
                <h3 className='headerText'>Appointment Statistics</h3>
                <Button label='Export' noAppearance={true} icon={<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path style={{ stroke: 'var(--text-tertiary)' }} d="M5.66675 7.70679L9.12873 11.1909L12.5934 7.70679" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                    <path style={{ stroke: 'var(--text-tertiary)' }} fill-rule="evenodd" clip-rule="evenodd" d="M10.0467 17.0294C4.81074 17.698 0.404078 13.2656 1.06608 7.99875C1.51741 4.41266 4.39607 1.51803 7.96074 1.06599C13.1901 0.403367 17.5901 4.82512 16.9354 10.0872C16.4894 13.6753 13.6121 16.5747 10.0467 17.0294Z" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                } iconPosition='left' size='small' />
            </div>
            <div className={styles.chartContainer}>
                <div className={styles.chart}>
                    <Doughnut data={buildData(labels, values, backgroundColors)} options={doughnutOptions} />
                </div>
                <ul className={styles.legends}>
                    {labels.map((label, index) => (
                        <li key={index} className={styles.legendContainer}>
                            <span className={styles.bullet} style={{ backgroundColor: backgroundColors[index] }}></span>
                            {label} <span className={styles.percentageValue}>{values[index]}%</span>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

export default AppointmentStatistics;
